<template>
  <div class="tw-mb-2 tw-block">
    <slot name="input-label">
      <label class="tw-block tw-text-sm tw-mb-2 opacity-78 tw-text-left" :for="name" :aria-label="name">
        {{ label }}
      </label>
    </slot>
    <label v-if="instruct.length > 0" class="tw-block tw-text-left tw-text-xs tw-mb-2 tw--mt-3 opacity-54">
      {{ instruct }}
    </label>

    <div class="quill-wrapper">
      <quillEditor
        :id="id"
        :placeholder="placeholder"
        :rows="rows"
        v-model="inputValue"
        :value="value"
        :class="inputClasses"
        :max-height="maxHeight"
        :options="editorOptions"
        ref="quillEditor"
      />
    </div>
    <!-- Hidden file input for image upload -->
    <input
      ref="imageInput"
      type="file"
      accept="image/*,video/*"
      style="display: none"
      @change="handleImageSelect"
    />
    <!-- :name="name" -->
    <slot name="error-message">
      <p class="tw-text-red-500 tw-text-xs tw-italic tw-text-left" v-if="showError" slot> {{ error }} </p>
    </slot>
  </div>
</template>

<script>
// import BaseTextarea from '@/components/BaseTextarea.vue';
import { quillEditor } from 'vue-quill-editor';
import { apiUrl } from '../modules/apiHelper';
import { urlFriendly } from '../modules/dataHelper';

let handler;

const SUPER_FORMAT_EDITOR_CONTAINER_OPTIONS = [
  [{ 'size': ['small', false, 'large', 'huge'] }],
  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  [{ 'indent': '-1' }, { 'indent': '+1' }],
  [{ 'font': [] }],
  [{ 'color': [] }],
  [{ 'align': [] }],
];

export default {
  name: 'QuillTextareaGroup',
  components: {
    // BaseTextarea,
    quillEditor,
  },
  props: {
    rows: {
      type: [String, Number],
      default: 1,
    },
    maxHeight: {
      type: String,
      default: 'auto',
    },
    label: {
      type: String,
      default: '',
      required: false,
    },
    instruct: {
      type: String,
      default: '',
      required: false,
    },
    id: {
      type: String,
      required: false,
    },
    name: {
      type: String,
      required: false,
    },
    value: {
      default: undefined,
    },
    inputClasses: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    error: {
      type: String,
      default: '',
      required: false,
    },
    formData: {
      type: Object,
      required: false,
    },
    allowMedia: {
      type: Boolean,
      default: false,
    },
    superFormat: {
      type: Boolean,
      default: false,
    },
    scrollingContainerSelector: {
      type: String,
      required: false,
      default: '.ql-container',
    },
  },
  data() {
    return {
      inputValue: this.value,
      editorOptions: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'link', ...(this.allowMedia ? ['image'] : [])],
              ['blockquote', 'code-block'],
              [],
              ...(this.superFormat ? SUPER_FORMAT_EDITOR_CONTAINER_OPTIONS : []),
            ],
            handlers: {
              image: this.imageHandler,
            },
          },
        },
      },
    };
  },
  computed: {
    showError() {
      return this.error.length > 0;
    },
  },
  watch: {
    inputValue(val) {
      this.$emit('input', val);
    },
    value(val) {
      this.inputValue = val;
    },
  },
  methods: {
    getScrollContainer() {
      if (this.scrollContainerSelector) {
        const el = document.querySelector(this.scrollContainerSelector);
        if (el) return el;
      }
      let el = this.$refs.quillEditor?.$el || this.$refs.quillEditor?.quill?.root;
      while (el) {
        const style = window.getComputedStyle(el);
        if (/(auto|scroll)/i.test(style.overflowY)) return el;
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    },
    imageHandler() {
      // Trigger file input when image button is clicked
      this.$refs.imageInput.click();
    },
    // Convert data URL or blob URL to File object
    async dataUrlToFile(dataUrl, filename) {
      let blob;
      
      if (dataUrl.startsWith('data:')) {
        // Handle data URL
        const response = await fetch(dataUrl);
        blob = await response.blob();
      } else if (dataUrl.startsWith('blob:')) {
        // Handle blob URL
        const response = await fetch(dataUrl);
        blob = await response.blob();
      } else {
        throw new Error('Invalid image source');
      }
      
      // Convert blob to File
      return new File([blob], filename, { type: blob.type || 'image/png' });
    },

    // Upload image and insert into editor
    async uploadAndInsertImage(file, quill, insertIndex = null) {
      const formData = {
        file1: file,
        ...(this.formData || {}),
      };

      try {
        this.$toasted.info('Uploading pasted image...', {
          duration: 3000,
          position: 'top-right',
        });

        const resource = await this.$store.dispatch('addContentMedia', formData);

        if (!resource) {
          throw new Error('Failed to save media');
        }

        // Use provided index or current selection
        let index;
        if (insertIndex !== null) {
          index = insertIndex;
        } else {
          const range = quill.getSelection(true);
          index = range ? range.index : quill.getLength();
        }
        
        const imageUrl = `${apiUrl}/${urlFriendly(resource.resource)}`;

        const imageHtml = `<img
          src="${imageUrl}"
          alt="${file.name}"
          loading="lazy"
          data-media-id="${resource.resourceid}"
        />`;

        quill.clipboard.dangerouslyPasteHTML(index, imageHtml);
        // Don't update selection here when insertIndex is provided
        // The caller will handle cursor positioning

        this.$toasted.success('Media uploaded and inserted', {
          duration: 3000,
          position: 'top-right',
        });
      } catch (error) {
        this.$toasted.global.appError({
          errorMessage: 'Failed to upload media',
        });
        console.error('Failed to upload media', error);
        throw error;
      }
    },
    
    async handlePasteEvent(event) {
      if (!this.allowMedia) return;
      
      const quill = this.$refs.quillEditor?.quill;
      if (!quill) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const scrollEl = this.getScrollContainer();
      const prevScrollTop = scrollEl?.scrollTop || 0;
      const prevEditorHeight = quill.container.clientHeight;

      const items = Array.from(clipboardData.items);
      const imageItems = items.filter(item => item.type.indexOf('image') !== -1);
      const htmlItem = items.find(item => item.type.indexOf('text/html') !== -1);
      
      let htmlContent = '';
      let htmlHasImages = false;
      
      // Load HTML content if available (needed for both image detection and text extraction)
      if (htmlItem) {
        htmlContent = await new Promise((resolve) => {
          htmlItem.getAsString((html) => {
            resolve(html);
          });
        });
        htmlHasImages = htmlContent && /<img[^>]+src=["'](blob:|data:)[^"']+["'][^>]*>/i.test(htmlContent);
      }
      
      // If there are images in clipboard items OR in HTML content, prevent default
      if (imageItems.length > 0 || htmlHasImages) {
        event.preventDefault();
        event.stopPropagation();
        
        // Get the current selection index before paste
        const range = quill.getSelection(true);
        const pasteIndex = range ? range.index : quill.getLength();
        
        // Check for text content
        const hasText = items.some(item => 
          item.type.indexOf('text/plain') !== -1 || 
          item.type.indexOf('text/html') !== -1
        );
        
        let textInserted = false;
        let afterTextIndex = pasteIndex;
        
        // If there's text, extract and paste it first (but remove images from HTML)
        if (hasText) {
          let textContent = '';
          
          // Prefer HTML content if available (preserves formatting)
          if (htmlItem && htmlContent) {
            // Remove img tags with blob/data URLs from HTML, but preserve other content
            textContent = htmlContent.replace(/<img[^>]+src=["'](blob:|data:)[^"']+["'][^>]*>/gi, '');
            // Clean up any empty paragraphs or divs left behind, but preserve structure
            textContent = textContent.replace(/<p[^>]*>\s*<\/p>/gi, '');
            textContent = textContent.replace(/<div[^>]*>\s*<\/div>/gi, '');
            // Trim but preserve HTML structure
            textContent = textContent.trim();
          }
          
          // If no HTML content or HTML was empty after removing images, try plain text
          if (!textContent || textContent.length === 0) {
            const textItem = items.find(item => item.type.indexOf('text/plain') !== -1);
            if (textItem) {
              // Get plain text content
              textContent = await new Promise((resolve) => {
                textItem.getAsString((text) => {
                  resolve(text);
                });
              });
            }
          }
          
          // Also try clipboardData.getData as fallback
          if (!textContent || textContent.trim().length === 0) {
            const clipboardText = clipboardData.getData('text/plain');
            if (clipboardText && clipboardText.trim().length > 0) {
              textContent = clipboardText;
            }
          }
          
          // Paste text content if we have it (check for actual content, not just whitespace/HTML tags)
          if (textContent) {
            // Check if there's actual text content
            let hasActualText = false;
            
            if (htmlItem && htmlContent) {
              // For HTML, check if there's text content after removing images
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = textContent;
              hasActualText = tempDiv.textContent && tempDiv.textContent.trim().length > 0;
            } else {
              // For plain text, just check if it's not empty
              hasActualText = textContent.trim().length > 0;
            }
            
            if (hasActualText) {
              if (htmlItem && htmlContent) {
                // Use HTML paste to preserve formatting
                quill.clipboard.dangerouslyPasteHTML(pasteIndex, textContent);
              } else {
                // Use plain text paste
                quill.insertText(pasteIndex, textContent);
              }
              textInserted = true;
              // Get the index after text is inserted
              afterTextIndex = quill.getLength();
            }
          }
        }
        
        // Now upload and insert images after the text (or at pasteIndex if no text)
        let currentInsertIndex = textInserted ? afterTextIndex : pasteIndex;
        
        // Process clipboard image items
        for (let i = 0; i < imageItems.length; i++) {
          const file = imageItems[i].getAsFile();
          if (file) {
            try {
              // Insert image at current position
              await this.uploadAndInsertImage(file, quill, currentInsertIndex);
              // Move insertion index forward (each image takes 1 position in delta)
              currentInsertIndex += 1;
            } catch (error) {
              console.error('Failed to upload pasted image:', error);
            }
          }
        }
        
        // Process images from HTML content (blob/data URLs)
        if (htmlHasImages) {
          const imgMatches = htmlContent.matchAll(/<img[^>]+src=["']((blob:|data:)[^"']+)["'][^>]*>/gi);
          for (const match of imgMatches) {
            const imageSrc = match[1];
            try {
              // Convert blob/data URL to file
              const file = await this.dataUrlToFile(imageSrc, 'pasted-image.png');
              // Insert image at current position
              await this.uploadAndInsertImage(file, quill, currentInsertIndex);
              // Move insertion index forward
              currentInsertIndex += 1;
            } catch (error) {
              console.error('Failed to upload image from HTML:', error);
            }
          }
        }
        
        // Set cursor after all content
        quill.setSelection(quill.getLength());
      } else {
        setTimeout(() => {
          if (scrollEl) {
            const currEditorHeight = quill.container.clientHeight;
            scrollEl.scrollTop = prevScrollTop + (currEditorHeight - prevEditorHeight);
          }
        }, 5);
      }
    },
    
    async handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      const quill = this.$refs.quillEditor?.quill;
      if (!quill) return;

      await this.uploadAndInsertImage(file, quill);
      this.$refs.imageInput.value = '';
    },
  },
  async mounted() {
    await this.$nextTick();

    if (this.allowMedia) {
      const quill = this.$refs.quillEditor?.quill;
      if (quill) {
        if (quill.root) {
          // Use paste event listener for better control
          quill.root.addEventListener('paste', this.handlePasteEvent, true);
        }
        
        // Add clipboard matcher as backup to catch images that slip through
        const clipboard = quill.getModule('clipboard');
        if (clipboard) {
          clipboard.addMatcher('IMG', (node, delta) => {
            // Check if image has blob or data URL
            if (node.src && (node.src.startsWith('blob:') || node.src.startsWith('data:'))) {
              // Return empty delta to prevent insertion
              // The paste handler will have already handled it
              const Delta = quill.constructor.import('delta');
              return new Delta();
            }
            // For external URLs, allow default behavior
            return delta;
          });
        }
      }
    }

    const quill = this.$refs.quillEditor?.quill;
    if (quill && quill.root) {
      handler = (e) => {
        e.preventDefault();
      }

      quill.root.closest('.quill-wrapper').querySelector('.ql-toolbar')?.addEventListener('mousedown', handler);
    }
  },

  beforeDestroy() {
    const quill = this.$refs.quillEditor?.quill;
    if (quill && quill.root) {
      quill.root.removeEventListener('paste', this.handlePasteEvent, true);

      if (handler) {
        quill.root.closest('.quill-wrapper').querySelector('.ql-toolbar')?.removeEventListener('mousedown', handler);
      }
      handler = null;
    }
  },
};
</script>

<style>
/* purgecss start ignore */
@import 'quill/dist/quill.core.css';
@import 'quill/dist/quill.snow.css';
@import 'quill/dist/quill.bubble.css';

.quill-wrapper .ql-toolbar {
  position: sticky;
  top: 0;        /* distance from top when sticking */
  background: white;
  z-index: 10;   /* ensure it stays above editor content */
  border-bottom: 1px solid #ccc;
}

/* purgecss end ignore */
</style>
